import AssessmentIcon from '@mui/icons-material/Assessment';
import BugReportIcon from '@mui/icons-material/BugReport';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DateRangeIcon from '@mui/icons-material/DateRange';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LayersIcon from '@mui/icons-material/Layers';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Tooltip as RechartTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import * as XLSX from 'xlsx';
import { useNotification } from '../../../../hooks/NotificationContext';
import { formatDate } from '../../../../lib/utils';
import { getCycleReport, getIterationReport } from '../../../../modules/testcycles';
import MultiSelectDropdown from '../../../elements/MultiSelectDropdown';
import ExportButton from '../export/ExportButton';
import { AutoGraph } from '@mui/icons-material';
import DetailsCard from '../../../../ui/layout/DetailsCard';

const PRIORITY_COLORS = {
  High: '#ef5350',
  Highest: '#b71c1c',
  Medium: '#d68910',
  Low: '#66bb6a',
  Lowest: '#81c784',
  Unknown: '#9e9e9e',
  Critical: '#ba4a00',
};

const NEW_VS_EXISTING_COLORS = {
  'New Defects': '#26a69a',
  'Existing Defects': '#1565c0',
};

const JIRA_STATUS_COLORS = [
  '#5c6bc0', '#26a69a', '#ef5350', '#ffa726', '#66bb6a',
  '#ab47bc', '#29b6f6', '#ff7043', '#8d6e63', '#78909c',
];
const getJiraStatusColor = (() => {
  const map = {};
  let idx = 0;
  return (status) => {
    if (!map[status]) { map[status] = JIRA_STATUS_COLORS[idx % JIRA_STATUS_COLORS.length]; idx++; }
    return map[status];
  };
})();

const StatCard = ({ label, value, icon, color = '#1976d2' }) => (
  <Card variant="outlined" sx={{ flex: 1, minWidth: 150, borderRadius: 2, transition: 'box-shadow .2s', '&:hover': { boxShadow: 3 } }}>
    <CardContent sx={{ pb: '12px !important' }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
        <Box sx={{ color }}>{icon}</Box>
        <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="h4" fontWeight={700} color={color}>{value ?? '—'}</Typography>
    </CardContent>
  </Card>
);

const ScopeRow = ({ label, values }) => {
  if (!values?.length) return null;
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Typography variant="body2" color="text.secondary" fontWeight={600}>
        {label}:
      </Typography>
      <Typography variant="body2" fontWeight={500} color="text.primary">
        {values.join(', ')}
      </Typography>
    </Stack>
  );
};

const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5, boxShadow: 2, minWidth: 160 }}>
      <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>{label}</Typography>
      {payload.map((p) => (
        <Stack key={p.dataKey} direction="row" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: p.fill }} />
            <Typography variant="caption" color="text.secondary">{p.name}</Typography>
          </Stack>
          <Typography variant="caption" fontWeight={600}>{p.value}</Typography>
        </Stack>
      ))}
    </Box>
  );
};

const EmptyChart = ({ message = 'No data available' }) => (
  <Box sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>
    <Typography variant="body2">{message}</Typography>
  </Box>
);

const TestCycleReport = ({ testCycleId, projectId, testCycleData }) => {
  const [selectedIterationIds, setSelectedIterationIds] = useState([]);
  const [generated, setGenerated] = useState(false);
  const [defectFilter, setDefectFilter] = useState('priority');
  const [isExporting, setIsExporting] = useState(false);
  const { addNotification } = useNotification();
  const [isPdfMode, setIsPdfMode] = useState(false);
  const summaryRef = useRef(null);
  const iterationRef = useRef(null);
  const iterationDefectsRef = useRef(null);

  const [isRegenerating, setIsRegenerating] = useState(false);
  const [hasInitializedIterations, setHasInitializedIterations] = useState(false);

  useEffect(() => {
    setHasInitializedIterations(false);
    setSelectedIterationIds([]);
  }, [testCycleId, projectId]);

  const {
    data: cycleReportRaw,
    isFetching: isCycleLoading,
    isError: isCycleError,
    refetch: refetchCycle,
  } = useQuery({
    queryKey: ['cycleReport', testCycleId, projectId],
    queryFn: () => getCycleReport(testCycleId, projectId),
    enabled: generated && !!testCycleId && !!projectId,
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: iterReportRaw,
    isFetching: isIterLoading,
    isError: isIterError,
    refetch: refetchIter,
  } = useQuery({
    queryKey: ['iterationReport', testCycleId, projectId, selectedIterationIds],
    queryFn: () => getIterationReport(testCycleId, projectId, selectedIterationIds),
    enabled: generated && !!testCycleId && !!projectId,
    staleTime: 5 * 60 * 1000,
  });

  const handleGenerate = useCallback(async () => {
    if (!testCycleId || !projectId) return;
    if (generated) {
      setIsRegenerating(true);
      await Promise.all([refetchCycle(), refetchIter()]);
      setIsRegenerating(false);
      addNotification('success', 'Report regenerated.');
    } else {
      setGenerated(true);
    }
  }, [testCycleId, projectId, generated, refetchCycle, refetchIter, addNotification]);

  const reportData = cycleReportRaw?.success ? cycleReportRaw.data : null;
  const iterationData = iterReportRaw?.success ? iterReportRaw.data : [];
  const allIterations = iterReportRaw?.all_iterations || [];

  useEffect(() => {
    if (isCycleError || isIterError) {
      addNotification('danger', 'Failed to generate report data.');
    }
  }, [isCycleError, isIterError, addNotification]);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (generated && allIterations.length > 0 && !hasInitializedIterations) {
      const allIds = allIterations.map((i) => i.id);
      if (iterReportRaw?.success) {
        queryClient.setQueryData(['iterationReport', testCycleId, projectId, allIds], iterReportRaw);
      }
      setSelectedIterationIds(allIds);
      setHasInitializedIterations(true);
    }
  }, [allIterations, generated, hasInitializedIterations, iterReportRaw, queryClient, testCycleId, projectId]);

  const loading = isCycleLoading && !reportData;
  const iterLoading = isIterLoading && !!reportData;

  const overallStatusChartData = useMemo(() => {
    return (reportData?.status_breakdown || []).map((s) => ({
      name: s.status_name,
      Count: s.count,
      color: s.color || '#90a4ae',
    }));
  }, [reportData]);

  const { iterStatusChartData, iterStatusKeys, iterStatusColorMap, hasIterStatusData } = useMemo(() => {
    const keys = [];
    const colorMap = {};
    const chartData = (iterationData || []).map((iter) => {
      const row = { name: iter.iteration_name };
      (iter.status_breakdown || []).forEach((s) => {
        row[s.status_name] = s.count;
        if (!colorMap[s.status_name]) colorMap[s.status_name] = s.color || '#90a4ae';
        if (!keys.includes(s.status_name)) keys.push(s.status_name);
      });
      return row;
    });
    const hasData = chartData.some((row) => keys.some((k) => row[k] > 0));
    return { iterStatusChartData: chartData, iterStatusKeys: keys, iterStatusColorMap: colorMap, hasIterStatusData: hasData };
  }, [iterationData]);

  const { iterDefectChartData, defectBarKeys, hasIterDefectData } = useMemo(() => {
    const priorityKeys = [];
    const statusKeys = [];

    const chartData = (iterationData || []).map((iter) => {
      const row = { name: iter.iteration_name };
      if (defectFilter === 'priority') {
        (iter.defects_by_priority || []).forEach((d) => {
          row[d.priority] = d.count;
          if (!priorityKeys.includes(d.priority)) priorityKeys.push(d.priority);
        });
      } else if (defectFilter === 'status') {
        (iter.defects_by_status || []).forEach((d) => {
          row[d.status] = d.count;
          if (!statusKeys.includes(d.status)) statusKeys.push(d.status);
        });
      } else {
        row['New Defects'] = iter.new_vs_existing?.new ?? 0;
        row['Existing Defects'] = iter.new_vs_existing?.existing ?? 0;
      }
      return row;
    });

    const barKeys =
      defectFilter === 'priority' ? priorityKeys :
        defectFilter === 'status' ? statusKeys :
          ['New Defects', 'Existing Defects'];

    const hasData = chartData.some((row) => barKeys.some((k) => (row[k] ?? 0) > 0));
    return { iterDefectChartData: chartData, defectBarKeys: barKeys, hasIterDefectData: hasData };
  }, [iterationData, defectFilter]);

  const getDefectChartDataByType = (type) => {
    return (iterationData || []).map((iter) => {
      const row = { name: iter.iteration_name };
      if (type === 'priority') {
        (iter.defects_by_priority || []).forEach((d) => { row[d.priority] = d.count; });
      } else if (type === 'status') {
        (iter.defects_by_status || []).forEach((d) => { row[d.status] = d.count; });
      } else {
        row['New Defects'] = iter.new_vs_existing?.new ?? 0;
        row['Existing Defects'] = iter.new_vs_existing?.existing ?? 0;
      }
      return row;
    });
  };

  const defectColorFor = (key, type) => {
    if (type === 'priority') return PRIORITY_COLORS[key] || '#90a4ae';
    if (type === 'status') return getJiraStatusColor(key);
    return NEW_VS_EXISTING_COLORS[key];
  };

  const pdfPriorityKeys = useMemo(() => {
    const s = new Set();
    iterationData.forEach(it => (it.defects_by_priority || []).forEach(d => s.add(d.priority)));
    return Array.from(s);
  }, [iterationData]);

  const pdfStatusKeys = useMemo(() => {
    const s = new Set();
    iterationData.forEach(it => (it.defects_by_status || []).forEach(d => s.add(d.status)));
    return Array.from(s);
  }, [iterationData]);

  const hasScopeData = [
    testCycleData?.platforms, testCycleData?.sub_platforms, testCycleData?.browsers,
    testCycleData?.geographies, testCycleData?.languages, testCycleData?.cloud_device,
  ].some((arr) => arr?.length > 0);

  const reportStartDate = reportData?.start_date ? formatDate(reportData.start_date) : null;
  const reportEndDate = reportData?.end_date ? formatDate(reportData.end_date) : null;

  const handleExportPDF = async () => {
    if (!summaryRef.current) return;

    setIsExporting(true);
    setIsPdfMode(true);
    await new Promise(resolve => setTimeout(resolve, 300)); // allow extra charts to render

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const margin = 10;
      const pdfWidth = pdf.internal.pageSize.getWidth();

      const canvas1 = await html2canvas(summaryRef.current, { scale: 2, useCORS: true });
      const img1 = canvas1.toDataURL('image/png');
      const height1 = (canvas1.height * (pdfWidth - margin * 2)) / canvas1.width;
      pdf.setFontSize(14);
      pdf.text(`${testCycleData?.key || ''} ${testCycleData?.name ? `- ${testCycleData.name}` : ''}`, margin, margin + 2);
      pdf.addImage(img1, 'PNG', margin, margin + 8, pdfWidth - margin * 2, height1);



      if (iterationDefectsRef.current) {
        const canvas3 = await html2canvas(iterationDefectsRef.current, { scale: 2, useCORS: true });
        const img3 = canvas3.toDataURL('image/png');
        const height3 = (canvas3.height * (pdfWidth - margin * 2)) / canvas3.width;
        pdf.addPage();
        pdf.addImage(img3, 'PNG', margin, margin, pdfWidth - margin * 2, height3);
      }

      pdf.save(`${testCycleData?.key || testCycleId}_Report.pdf`);
      addNotification('success', 'PDF exported successfully.');
    } catch (err) {
      addNotification('danger', 'Failed to export PDF.');
    } finally {
      setIsPdfMode(false);
      setIsExporting(false);
    }
  };
  const handleExportExcel = async () => {
    if (!reportData) return;
    setIsExporting(true);

    try {
      const wb = XLSX.utils.book_new();
      const sanitizeSheetName = (name = "Sheet") =>
        name.replace(/[:\\/?*\[\]]/g, '').substring(0, 31) || "Sheet";

      const usedNames = new Set();
      const getUniqueSheetName = (name) => {
        let clean = sanitizeSheetName(name);
        let finalName = clean;
        let counter = 1;

        while (usedNames.has(finalName)) {
          finalName = `${clean}_${counter++}`.substring(0, 31);
        }

        usedNames.add(finalName);
        return finalName;
      };

      const autoFitColumns = (data) => {
        if (!data || data.length === 0) return [];
        return Object.keys(data[0]).map((key) => ({
          wch: Math.max(
            key.length,
            ...data.map(row => String(row[key] ?? '').length)
          ) + 2
        }));
      };

      const addSheet = (data, name) => {
        const safeData = data?.length ? data : [{ Info: "No data available" }];
        const ws = XLSX.utils.json_to_sheet(safeData);
        ws['!cols'] = autoFitColumns(safeData);
        XLSX.utils.book_append_sheet(wb, ws, getUniqueSheetName(name));
      };

      addSheet(
        overallStatusChartData.map(({ color, ...rest }) => rest),
        'Overall Status'
      );

      addSheet([
        { Type: 'New', Count: reportData.defect_summary?.new_vs_existing?.new ?? 0 },
        { Type: 'Existing', Count: reportData.defect_summary?.new_vs_existing?.existing ?? 0 },
        { Type: 'Total', Count: reportData.defect_summary?.new_vs_existing?.total ?? 0 },
      ], 'Defects - New vs Existing');

      const priorityData = (reportData.defect_summary?.by_priority || []).map(d => ({
        Priority: d.priority,
        Count: d.count
      }));

      if (priorityData.length > 0) {
        priorityData.push({
          Priority: 'Total',
          Count: priorityData.reduce((sum, r) => sum + r.Count, 0)
        });
      }

      addSheet(priorityData, 'Defects - Priority');
      const statusData = (reportData.defect_summary?.by_status || []).map(d => ({
        Status: d.status,
        Count: d.count
      }));

      if (statusData.length > 0) {
        statusData.push({
          Status: 'Total',
          Count: statusData.reduce((sum, r) => sum + r.Count, 0)
        });
      }

      addSheet(statusData, 'Defects - Status');

      addSheet(iterStatusChartData, 'Iteration Execution');
      addSheet(getDefectChartDataByType('priority'), 'Iter Defects - Priority');
      addSheet(getDefectChartDataByType('status'), 'Iter Defects - Status');
      addSheet(getDefectChartDataByType('newVsExisting'), 'Iter Defects - New/Existing');
      const fileName = `${testCycleData?.key || testCycleId}_Report.xlsx`;

      try {
        XLSX.writeFile(wb, fileName);
      } catch (err) {
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/octet-stream' });

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
      }

      addNotification('success', 'Excel exported successfully.');
    } catch (error) {
      console.error("Excel export error:", error);
      addNotification('danger', 'Failed to export Excel.');
    } finally {
      setIsExporting(false);
    }
  };

  const iterationOptions = allIterations.map((i) => ({
    id: i.id,
    name: i.name,
  }));

  const overallBarSize = Math.max(20, Math.min(40, Math.floor(320 / Math.max(overallStatusChartData.length, 1))));
  const iterBarSize = Math.max(16, Math.min(36, Math.floor(480 / Math.max((iterationData || []).length, 1))));
  const cardActions = <Stack direction="row" spacing={1}>
    {generated && (
      <ExportButton
        tooltip="Export Test Cycle Report"
        onExport={(type) => type === 'pdf' ? handleExportPDF() : handleExportExcel()}
        isExporting={isExporting}
      />
    )}
    <Button
      variant="contained" size="medium"
      startIcon={(loading || isRegenerating) ? <CircularProgress size={16} color="inherit" /> : <AssessmentIcon />}
      onClick={handleGenerate}
      disabled={loading || isRegenerating || isExporting}>
      {(loading || isRegenerating) ? 'Generating…' : generated ? 'Regenerate' : 'Generate'}
    </Button>
  </Stack>
  return (
    <DetailsCard title='Reports' actions={cardActions} >
      {!generated && !loading && (
        <Box sx={{ textAlign: 'center', py: 10, color: 'text.disabled' }}>
          <AssessmentIcon sx={{ fontSize: 56, mb: 1, opacity: 0.3 }} />
          <Typography variant="body1" fontWeight={500}>Click "Generate Report" to get started</Typography>
          <Typography variant="caption">Cycle-level and iteration-level data will appear here.</Typography>
        </Box>
      )}

      {generated && reportData && (
        <Box sx={{ px: 2 }}>
          <Box ref={summaryRef} sx={{ pb: 1 }}>
            <Box sx={{ mb: 3, p: 2.5, bgcolor: '#fbfcff', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              {(reportStartDate || reportEndDate) && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <DateRangeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.primary" fontWeight={500}>
                    {reportStartDate || 'N/A'} {reportEndDate ? ` — ${reportEndDate}` : ' — Ongoing'}
                  </Typography>
                </Stack>
              )}

              {hasScopeData && (reportStartDate || reportEndDate) && (
                <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />
              )}

              {hasScopeData && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                  <ScopeRow label="Platforms" values={testCycleData?.platforms} />
                  <ScopeRow label="Sub-Platforms" values={testCycleData?.sub_platforms} />
                  <ScopeRow label="Browsers" values={testCycleData?.browsers} />
                  <ScopeRow label="Geographies" values={testCycleData?.geographies} />
                  <ScopeRow label="Languages" values={testCycleData?.languages} />
                  <ScopeRow label="Cloud Device" values={testCycleData?.cloud_device} />
                </Box>
              )}
            </Box>

            <Stack direction="row" flexWrap="wrap" gap={3} mb={4}>
              <StatCard label="Test Cases" value={reportData.total_test_cases} icon={<CheckCircleOutlineIcon />} color="#1976d2" />
              <StatCard label="Iterations" value={reportData.iteration_count} icon={<LayersIcon />} color="#0288d1" />
              <StatCard label="Total Defects" value={reportData.defect_summary?.new_vs_existing?.total ?? 0} icon={<BugReportIcon />} color="#d32f2f" />
            </Stack>

            <Row>
              <Col xs={12} md={7} className="mb-3">
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} mb={2}>Overall Execution Status</Typography>
                    {overallStatusChartData.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 6, color: 'text.disabled' }}>
                        <AutoGraph sx={{ fontSize: 40, mb: 1.5, opacity: 0.3 }} />
                        <Typography variant="body2">No executions recorded yet.</Typography>
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={overallStatusChartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }} barSize={overallBarSize}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                          <RechartTooltip content={<CustomBarTooltip />} />
                          <Bar dataKey="Count" radius={[4, 4, 0, 0]}>
                            {overallStatusChartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
              </Col>

              <Col xs={12} md={5} className="mb-3">
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} mb={2}>Overall Defect Summary</Typography>

                    {reportData.defect_summary?.new_vs_existing?.total > 0 ? (
                      <>
                        <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
                          <Typography variant="overline" color="text.secondary">New vs Existing</Typography>
                          <Tooltip title="This calculation is based on test cycle creation date" placement="top" arrow>
                            <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'default' }} />
                          </Tooltip>
                        </Stack>
                        <Stack direction="row" spacing={3} mb={3}>
                          {[
                            { label: 'New', value: reportData.defect_summary?.new_vs_existing?.new, color: '#ef5350' },
                            { label: 'Existing', value: reportData.defect_summary?.new_vs_existing?.existing, color: '#ffa726' },
                            { label: 'Total', value: reportData.defect_summary?.new_vs_existing?.total, color: '#1976d2' },
                          ].map((item) => (
                            <Box key={item.label} textAlign="center" sx={{ flex: 1, bgcolor: `${item.color}11`, p: 1.5, borderRadius: 2, border: `1px solid ${item.color}33` }}>
                              <Typography variant="h5" fontWeight={700} color={item.color}>{item.value ?? 0}</Typography>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>{item.label}</Typography>
                            </Box>
                          ))}
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="overline" color="text.secondary" display="block" mb={0.5}>By Priority</Typography>
                        <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
                          {(reportData.defect_summary?.by_priority || []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No defects linked</Typography>
                          ) : (reportData.defect_summary?.by_priority || []).map((d) => (
                            <Chip key={d.priority} label={`${d.priority}: ${d.count}`} size="small" sx={{
                              fontWeight: 600,
                              backgroundColor: `${PRIORITY_COLORS[d.priority] || '#9e9e9e'}22`,
                              color: PRIORITY_COLORS[d.priority] || '#555',
                              border: `1px solid ${PRIORITY_COLORS[d.priority] || '#9e9e9e'}55`,
                            }} />
                          ))}
                        </Stack>

                        <Typography variant="overline" color="text.secondary" display="block" mb={0.5}>By Status</Typography>
                        <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
                          {(reportData.defect_summary?.by_status || []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No defects linked</Typography>
                          ) : (reportData.defect_summary?.by_status || []).map((d) => (
                            <Chip key={d.status} label={`${d.status}: ${d.count}`} size="small" sx={{
                              fontWeight: 600,
                              backgroundColor: `${getJiraStatusColor(d.status)}22`,
                              color: getJiraStatusColor(d.status),
                              border: `1px solid ${getJiraStatusColor(d.status)}55`,
                            }} />
                          ))}
                        </Stack>
                      </>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 6, color: 'text.disabled' }}>
                        <BugReportIcon sx={{ fontSize: 40, mb: 1.5, opacity: 0.3 }} />
                        <Typography variant="body2">No defects linked to this test cycle.</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Col>
            </Row>

            {allIterations.length > 0 && (
              <Box px={1} mt={1} ref={iterationRef}>
                <Divider sx={{ mb: 3 }} />

                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" mb={2} gap={2}>
                  <Typography variant="subtitle1" fontWeight={700}>Iteration-wise Breakdown</Typography>
                  <Box sx={{ minWidth: 280, maxWidth: 380 }} data-html2canvas-ignore="true">
                    <MultiSelectDropdown
                      options={iterationOptions}
                      selectedValues={selectedIterationIds}
                      onChange={setSelectedIterationIds}
                      placeholder="Filter iterations…"
                      name="iterations"
                      showSelectAll
                      searchable={iterationOptions.length > 5}
                      sx={{ mb: 0 }}
                    />
                  </Box>
                </Stack>

                {iterLoading ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={32} /></Box>
                ) : (iterationData && iterationData.length > 0 && selectedIterationIds.length > 0) ? (
                  <>
                    <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight={700} mb={2}>Test Executions</Typography>
                        {!hasIterStatusData ? (
                          <EmptyChart message="No execution data for selected iterations." />
                        ) : (
                          <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={iterStatusChartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }} barSize={iterBarSize}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                              <RechartTooltip content={<CustomBarTooltip />} />
                              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                              {iterStatusKeys.map((key) => (
                                <Bar key={key} dataKey={key} stackId="status" fill={iterStatusColorMap[key]} />
                              ))}
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </CardContent>
                    </Card>

                    <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }} data-html2canvas-ignore="true">
                      <CardContent>
                        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" mb={2} gap={1}>
                          <Typography variant="subtitle2" fontWeight={700}>Defects Linked</Typography>
                          <ToggleButtonGroup
                            value={defectFilter} exclusive
                            onChange={(_, v) => v && setDefectFilter(v)}
                            size="small"
                            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 12, px: 1.5, py: 0.4 } }}
                          >
                            <ToggleButton value="priority">By Priority</ToggleButton>
                            <ToggleButton value="status">By Status</ToggleButton>
                            <ToggleButton value="newVsExisting">New vs Existing</ToggleButton>
                          </ToggleButtonGroup>
                        </Stack>
                        {!hasIterDefectData ? (
                          <EmptyChart message="No defect data for selected iterations." />
                        ) : (
                          <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={iterDefectChartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }} barSize={iterBarSize}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                              <RechartTooltip content={<CustomBarTooltip />} />
                              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                              {defectBarKeys.map((key) => <Bar key={key} dataKey={key} stackId="defect" fill={defectColorFor(key, defectFilter)} />)}
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                        {defectFilter === 'newVsExisting' && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                            <Typography variant="caption" color="text.disabled" fontStyle="italic">
                              * Based on execution creation date
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <Box sx={{
                    textAlign: 'center',
                    py: 10,
                    bgcolor: '#fbfcff',
                    borderRadius: 3,
                    border: '1px dashed',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <LayersIcon sx={{ fontSize: 48, color: 'text.disabled', opacity: 0.5, mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight={700} color="text.secondary">
                      {selectedIterationIds.length === 0 ? 'No Iterations Selected' : 'No Data Available'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 350, mx: 'auto' }}>
                      {selectedIterationIds.length === 0
                        ? 'Select at least one iteration to view the breakdown.'
                        : 'No data found for the selected iterations.'}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
      {isPdfMode && generated && iterationData.length > 0 && (
        <Box sx={{
          position: 'fixed', left: -5000, top: 0,
          width: 900, bgcolor: 'white', p: 3
        }} ref={iterationDefectsRef}>
          <Typography variant="h6" fontWeight={700} mb={3}>Iteration-wise Defect Breakdown</Typography>

          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" fontWeight={700} mb={1}>By Priority</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={getDefectChartDataByType('priority')} margin={{ top: 5, right: 30, left: 20, bottom: 5 }} barSize={iterBarSize}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {pdfPriorityKeys.map(k => <Bar key={k} dataKey={k} stackId="pdf_priority" fill={defectColorFor(k, 'priority')} />)}
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" fontWeight={700} mb={1}>By Status</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={getDefectChartDataByType('status')} margin={{ top: 5, right: 30, left: 20, bottom: 5 }} barSize={iterBarSize}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {pdfStatusKeys.map(k => <Bar key={k} dataKey={k} stackId="pdf_status" fill={defectColorFor(k, 'status')} />)}
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <Box>
            <Typography variant="body1" fontWeight={700} mb={1}>New vs Existing</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={getDefectChartDataByType('newVsExisting')} margin={{ top: 5, right: 30, left: 20, bottom: 5 }} barSize={iterBarSize}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="New Defects" stackId="pdf_new_existing" fill={NEW_VS_EXISTING_COLORS['New Defects']} />
                <Bar dataKey="Existing Defects" stackId="pdf_new_existing" fill={NEW_VS_EXISTING_COLORS['Existing Defects']} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      )}
    </DetailsCard>
  );
};

export default TestCycleReport;